import type { ISearchBoxStyles } from '@fluentui/react/lib/SearchBox';
import { getChatThemeTokens, type IdigestChatTheme } from '../theme';

/**
 * Inner Fluent SearchBox styles. Always pair with <SearchBoxChrome> —
 * border / focus color live on the wrapper (avoids Fluent clear-btn covering corners).
 */
export function getSearchBoxStyles(theme: IdigestChatTheme = 'light'): Partial<ISearchBoxStyles> {
  const tokens = getChatThemeTokens(theme);
  const rootChrome = {
    width: '100%',
    maxWidth: '100%',
    height: '42px',
    minHeight: '42px',
    margin: 0,
    boxSizing: 'border-box' as const,
    backgroundColor: 'transparent',
    border: 'none',
    borderWidth: 0,
    borderRadius: 0,
    color: tokens.fg,
    outline: 'none',
    boxShadow: 'none'
  };
  return {
    root: {
      ...rootChrome,
      selectors: {
        ':focus::after': {
          display: 'none',
          border: 'none'
        },
        '::after': {
          display: 'none',
          border: 'none'
        }
      }
    },
    field: {
      color: tokens.fg,
      backgroundColor: 'transparent',
      fontSize: '15px'
    },
    icon: {
      color: tokens.searchIcon
    },
    clearButton: {
      width: 32,
      height: '100%',
      margin: 0,
      alignSelf: 'center' as const,
      selectors: {
        '.ms-Button': {
          boxShadow: 'none',
          border: 'none',
          borderRadius: 8,
          backgroundColor: 'transparent',
          height: '100%',
          width: 32,
          minWidth: 32
        },
        '.ms-Button:hover, .ms-Button:active, .ms-Button:focus': {
          boxShadow: 'none',
          backgroundColor: tokens.surfaceHover
        }
      }
    }
  };
}

export function getGroupButtonStyles(theme: IdigestChatTheme = 'light') {
  const tokens = getChatThemeTokens(theme);
  const interactive = {
    backgroundColor: tokens.surfaceHover,
    color: tokens.fg,
    border: `1px solid ${tokens.buttonBorder}`
  };
  return {
    flexContainer: {
      justifyContent: 'left' as const,
      marginLeft: 0
    },
    root: {
      justifyContent: 'flex-start' as const,
      border: `1px solid ${tokens.buttonBorder}`,
      borderRadius: '10px',
      padding: '10px',
      height: '42px',
      width: '100%',
      fontSize: '15px',
      boxShadow: tokens.buttonShadow,
      backgroundColor: tokens.surface,
      color: tokens.fg
    },
    rootHovered: interactive,
    rootPressed: interactive,
    rootFocused: {
      backgroundColor: tokens.surface,
      color: tokens.fg,
      border: `1px solid ${tokens.buttonBorder}`
    },
    label: {
      color: tokens.fg,
      fontWeight: 400 as const
    },
    labelHovered: {
      color: tokens.fg
    },
    labelPressed: {
      color: tokens.fg
    }
  };
}

export const COMMON_DIALOG_CONTENT_STYLES = {
  position: 'relative' as const,
  display: 'flex',
  flexDirection: 'column' as const,
  // Auto height follows content; modal shell owns maxHeight.
  height: 'auto' as const,
  maxHeight: 'inherit' as const,
  visibility: 'visible' as const,
  overflowY: 'auto' as const,
  overflowX: 'hidden' as const,
  overscrollBehavior: 'contain' as const,
  WebkitOverflowScrolling: 'touch' as const,
  // Horizontal inset owned by searchbox / list wrappers (same as fellowship pane).
  padding: '0 0 12px',
  minHeight: 0,
  boxSizing: 'border-box' as const,
  backgroundColor: 'var(--idigest-chat-dialog-bg, #ffffff)',
  color: 'var(--idigest-chat-dialog-fg, #333333)'
};

export const COMMON_HEADER_BUTTON_STYLES = {
  root: {
    padding: '8px',
    border: 'none',
    background: 'transparent',
    minWidth: '30px',
    height: '30px'
  },
  flexContainer: {
    height: '100%'
  }
};

/** @deprecated Prefer getSearchBoxStyles(theme) */
export const COMMON_SEARCH_BOX_STYLES = getSearchBoxStyles('light');

/** @deprecated Prefer getGroupButtonStyles(theme) */
export const COMMON_GROUP_BUTTON_STYLES = getGroupButtonStyles('light');
